const { addDelegatedEventListener } = require('../common/utils/domEvents');
const { forEachElement, resolveElements } = require('../common/utils/dom');

const OPEN_CLASS = 'store-opening-times--open';
const OPENING_TIMES = '.store-opening-times';
const OPEN_BUTTON = `${OPENING_TIMES}__toggler--open`;

// `addDelegatedEventListener` registers a fresh closure on every call and hands
// back no way to remove it, so each container is bound once: callers run this
// after every injection and would otherwise stack handlers on the same node.
const boundContainers = new WeakSet();

/**
 * Opens the table when the open link was clicked, closes it otherwise.
 * @param {MouseEvent} event - click on a descendant of an opening-times block
 * @returns {void}
 */
function handleClick(event) {
  // The delegation only fires for clicks inside an opening-times block, so the
  // event is ours to swallow — outside it, the rest of the card keeps its
  // clicks, which is what selecting a store needs.
  event.preventDefault();
  event.stopPropagation();

  const openingTimes = event.target.closest(OPENING_TIMES);
  openingTimes.classList.toggle(OPEN_CLASS, event.target.matches(OPEN_BUTTON));
}

/**
 * Makes every opening-times table inside a container expand and collapse.
 *
 * The listener is delegated, so it has to be bound on whatever element the
 * cards were injected into: the store card markup travels — the checkout
 * summary, the PDP modal and the PDP selected-store card all clone it — and a
 * clone lands outside whatever container was bound before it.
 *
 * It lives apart from `storeLocator/index` so that a cartridge can bind the
 * toggle without pulling that module in: `index` requires `./map`, which reads
 * `window.FRONT_CONFIG.responsive` while it loads, and dragging the map into
 * the PDP bundle to wire a click handler is not a trade worth making.
 *
 * Calling it again on a container already bound is harmless, so a caller may
 * run it after every injection without tracking what it bound.
 * @param {string|Element|NodeList|Element[]} target - what the cards went into
 * @returns {void}
 */
function bindOpeningTimesToggle(target) {
  resolveElements(target).forEach((container) => {
    if (!boundContainers.has(container)) {
      addDelegatedEventListener(container, 'click', OPENING_TIMES, handleClick);
      boundContainers.add(container);
    }

    // Anything carried over from a previous render starts closed.
    forEachElement(container.querySelectorAll(`.${OPEN_CLASS}`), (openTable) =>
      openTable.classList.remove(OPEN_CLASS)
    );
  });
}

module.exports = bindOpeningTimesToggle;
